import { useEffect, useRef, useState } from "react";

import type { AiCreditPackOption } from "./creditPacks";

/**
 * GAU pack dropdown for seatless checkout surfaces (credit-card gate, AI
 * quota paywall). Mirrors the Manage Plan modal's credit-pack dropdown
 * styling (trigger chrome, menu card, hover/selected states) so pack
 * selection looks the same everywhere — a native <select> renders the OS
 * menu, which doesn't match the platform.
 */
export function CreditPackSelect({
  options,
  selectedCredits,
  onSelect,
  billingInterval,
  dataTest = "credit-pack-select",
}: {
  options: AiCreditPackOption[];
  selectedCredits: number;
  onSelect: (credits: number) => void;
  billingInterval: "annual" | "monthly";
  dataTest?: string;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const formatLabel = (option: AiCreditPackOption) =>
    `${option.credits.toLocaleString()} GAUs — $${option.priceUsd.toLocaleString()}${intervalSuffix}`;
  const selected =
    options.find((option) => option.credits === selectedCredits) ?? options[0];

  return (
    <div ref={wrapperRef} className="relative w-full">
      <button
        type="button"
        data-test={dataTest}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-10 w-full cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-white px-3 py-2 text-left shadow-[0_0_0_1px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb]"
      >
        <span className="flex-1 truncate text-sm leading-4 text-[#454d5f]">
          {formatLabel(selected)}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 text-[#6c7689] transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>
      {open ? (
        <div
          role="listbox"
          data-test={`${dataTest}-menu`}
          className="absolute left-0 right-0 top-[calc(100%+4px)] z-[2000] max-h-[320px] overflow-y-auto overflow-x-hidden rounded-lg border border-[#e8eaed] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]"
        >
          {options.map((option) => {
            const isSelected = option.credits === selected.credits;
            return (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                data-test={`${dataTest}-option-${option.credits}`}
                onClick={() => {
                  onSelect(option.credits);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between border-0 px-3 py-2.5 text-left text-sm transition ${isSelected ? "cursor-pointer bg-[#f9fafb] text-[#1f2633]" : "cursor-pointer bg-white text-[#1f2633] hover:bg-[#f9fafb]"}`}
              >
                <span>{formatLabel(option)}</span>
                <span
                  className={`text-sm leading-none ${isSelected ? "text-[#1f2633]" : "text-transparent"}`}
                >
                  ✓
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
